import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { sliderPosToTokens, snapTemperature, tokensToSliderPos } from '../../lib/llmModelGenerationParams'

const labelStyle = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#475569',
  marginBottom: 6,
}

const valueStyle = {
  fontSize: 15,
  fontWeight: 700,
  color: '#0f172a',
  marginBottom: 8,
}

const cardWrap = {
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: 14,
  marginBottom: 16,
}

const trackWrap = {
  position: 'relative',
  marginBottom: 4,
}

const rangeStyle = {
  width: '100%',
  height: 10,
  margin: 0,
  cursor: 'grab',
  accentColor: '#2563eb',
}

const endLabels = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 11,
  color: '#94a3b8',
  marginTop: 4,
}

function formatTokenDisplay(n) {
  return Number(n).toLocaleString()
}

function formatTempDisplay(n) {
  return Number(n).toFixed(2)
}

/**
 * Horizontal range slider for max tokens (log scale) or temperature (linear).
 */
export default function GenerationParamSlider({
  mode,
  limits,
  value,
  useDefault,
  onUseDefaultChange,
  onChange,
  disabled = false,
  label,
  modelHint = '',
}) {
  const hidden = mode === 'temperature' && !limits.temperature.supported

  const [dragging, setDragging] = useState(false)
  const skipEffect = useRef(false)

  const min = mode === 'tokens' ? limits.maxOutputTokens.min : limits.temperature.min
  const max = mode === 'tokens' ? limits.maxOutputTokens.max : limits.temperature.max
  const def = mode === 'tokens' ? limits.maxOutputTokens.default : limits.temperature.default

  const effectiveValue = useDefault ? null : value != null ? value : def

  const sliderPos = useMemo(() => {
    if (useDefault || effectiveValue == null) {
      if (mode === 'tokens') return tokensToSliderPos(def, min, max)
      return (def - min) / (max - min || 1)
    }
    if (mode === 'tokens') return tokensToSliderPos(effectiveValue, min, max)
    return (effectiveValue - min) / (max - min || 1)
  }, [mode, useDefault, effectiveValue, def, min, max])

  const [pos, setPos] = useState(sliderPos)

  useEffect(() => {
    if (!dragging && !skipEffect.current) setPos(sliderPos)
    skipEffect.current = false
  }, [sliderPos, dragging])

  const applyPos = useCallback(
    (p) => {
      const clamped = Math.max(0, Math.min(1, p))
      if (mode === 'tokens') {
        const n = sliderPosToTokens(clamped, min, max)
        onChange(n)
      } else {
        const raw = min + clamped * (max - min)
        const n = snapTemperature(raw, limits)
        onChange(n)
      }
    },
    [mode, min, max, limits, onChange]
  )

  const onInput = (e) => {
    const p = Number(e.target.value)
    setPos(p)
    if (useDefault) {
      onUseDefaultChange(false)
      skipEffect.current = true
    }
    applyPos(p)
  }

  const onPointerDown = () => setDragging(true)
  const onPointerUp = () => setDragging(false)

  const isDisabled = disabled

  const defaultLabel =
    mode === 'temperature' ? 'Use provider default' : 'Use model default'

  const display =
    useDefault || effectiveValue == null
      ? `Default (${mode === 'tokens' ? formatTokenDisplay(def) : formatTempDisplay(def)})`
      : mode === 'tokens'
        ? formatTokenDisplay(effectiveValue)
        : formatTempDisplay(effectiveValue)

  if (hidden) return null

  return (
    <div style={cardWrap}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={useDefault}
          disabled={disabled}
          onChange={(e) => onUseDefaultChange(e.target.checked)}
        />
        <span style={{ color: '#334155' }}>
          {defaultLabel} ({mode === 'tokens' ? formatTokenDisplay(def) : formatTempDisplay(def)})
        </span>
      </label>

      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{display}</div>

      <div style={trackWrap}>
        <input
          type="range"
          min={0}
          max={1}
          step={mode === 'tokens' ? 0.001 : limits.temperature.step / (max - min || 1)}
          value={pos}
          disabled={isDisabled}
          onInput={onInput}
          onChange={onInput}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerLeave={() => setDragging(false)}
          style={{
            ...rangeStyle,
            cursor: isDisabled ? 'not-allowed' : dragging ? 'grabbing' : 'grab',
          }}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={effectiveValue ?? def}
        />
      </div>
      <div style={endLabels}>
        <span>{mode === 'tokens' ? formatTokenDisplay(min) : formatTempDisplay(min)}</span>
        <span>{mode === 'tokens' ? formatTokenDisplay(max) : formatTempDisplay(max)}</span>
      </div>
      <p style={{ fontSize: 12, color: '#94a3b8', margin: '8px 0 0' }}>
        Drag left to decrease, right to increase
        {modelHint ? ` · ${modelHint}` : ''}
      </p>
      {!useDefault ? (
        <button
          type="button"
          style={{
            marginTop: 8,
            padding: 0,
            border: 'none',
            background: 'none',
            color: '#2563eb',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
          onClick={() => {
            onUseDefaultChange(true)
            onChange(null)
          }}
        >
          Reset to model default
        </button>
      ) : null}
    </div>
  )
}
